import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LockKey, CheckCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!token) { toast.error("Missing reset token."); return; }
    if (password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (password !== confirm) { toast.error("Passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="App min-h-screen flex flex-col">
      <Navbar />
      <section className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="max-w-md w-full border border-border bg-white p-8">
          <div className="text-xs font-mono tracking-widest-2 uppercase text-muted-foreground mb-3">Reset password</div>
          <h1 className="font-heading text-3xl font-bold mb-2">Choose a new password</h1>
          {done ? (
            <div className="mt-6" data-testid="reset-success">
              <CheckCircle size={40} weight="fill" className="text-primary mb-4" />
              <p className="text-sm leading-relaxed mb-6">
                Your password has been reset. Sign in with your new password to continue.
              </p>
              <Button onClick={() => navigate("/login")} className="rounded-sm" data-testid="reset-goto-login">
                Sign in
              </Button>
            </div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-8">Enter a new password below. It must be at least 8 characters.</p>
              <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
                <div>
                  <Label htmlFor="new-password" className="text-xs">New password</Label>
                  <div className="relative mt-1.5">
                    <LockKey size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="new-password"
                      type="password"
                      required
                      minLength={8}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      className="pl-9 rounded-sm h-11"
                      data-testid="reset-password-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="confirm-password" className="text-xs">Confirm new password</Label>
                  <div className="relative mt-1.5">
                    <LockKey size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="confirm-password"
                      type="password"
                      required
                      minLength={8}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      placeholder="Type it again"
                      className="pl-9 rounded-sm h-11"
                      data-testid="reset-confirm-input"
                    />
                  </div>
                </div>
                <Button type="submit" disabled={busy} className="w-full h-11 rounded-sm" data-testid="reset-submit-btn">
                  {busy ? "Resetting…" : "Reset password"}
                </Button>
                <p className="text-sm text-muted-foreground text-center pt-2">
                  <Link to="/login" className="underline font-medium text-foreground">Back to sign in</Link>
                </p>
              </form>
            </>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
